import { format, parseISO } from 'date-fns';
import {
  DEFAULT_DATE_FORMAT,
  ExportPointsTransactionsRequest,
  PointsTransaction,
} from '@aslanpay/portal-common-lib';

export const GIFTING_EXPORT_SIZE = 50000;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface GiftingTransactionGroup {
  groupId: string;
  date: string;
  reason: string;
  displayReason: string;
  fullNotes: string;
  totalAmount: number;
  recipients: number;
  transactions: PointsTransaction[];
  transactionIds: string[];
}

/**
 * Get the group ID for a transaction, creating a synthetic one if needed
 */
export const getTransactionGroupId = (transaction: PointsTransaction): string => {
  let groupId = transaction.transactionGroupId;

  if (!groupId) {
    const timestamp = new Date(transaction.createdAt).getTime();
    const notes = transaction.notes || '';
    const timeWindow = Math.floor(timestamp / 60000); // Group by minute
    groupId = `ungrouped_${timeWindow}_${notes.substring(0, 20).replace(/[^a-zA-Z0-9]/g, '')}`;
  }

  return groupId;
};

/**
 * Whether a group ID is a real transaction group from the backend, rather than the synthetic
 * placeholder built on screen for transactions that have no group
 */
export const isRealTransactionGroupId = (groupId: string): boolean => UUID_PATTERN.test(groupId);

/**
 * Export params for one gifting row. Only real groups with more than one recipient export by
 * group ID; everything else, including synthetic groups, exports by its transaction IDs.
 */
export const getGiftingExportParams = (group: {
  groupId: string;
  recipients: number;
  transactionIds: string[];
}): ExportPointsTransactionsRequest => {
  if (group.recipients > 1 && isRealTransactionGroupId(group.groupId)) {
    return { transactionGroupIds: [group.groupId], size: GIFTING_EXPORT_SIZE };
  }

  return { ids: group.transactionIds, size: GIFTING_EXPORT_SIZE };
};

export interface BulkGiftingExport {
  params: ExportPointsTransactionsRequest | null;
  skippedCount: number;
}

/**
 * Export params for several gifting rows. The export ANDs its filters, so real groups and the
 * transaction IDs of synthetic groups can't share one request: real groups win and the synthetic
 * ones are skipped (and counted). With only synthetic groups selected, they export by transaction IDs.
 */
export const getBulkGiftingExport = (groups: GiftingTransactionGroup[]): BulkGiftingExport => {
  const realGroupIds = groups
    .map((group) => group.groupId)
    .filter((groupId) => isRealTransactionGroupId(groupId));
  const syntheticGroups = groups.filter((group) => !isRealTransactionGroupId(group.groupId));

  if (realGroupIds.length > 0) {
    return {
      params: { transactionGroupIds: realGroupIds, size: GIFTING_EXPORT_SIZE },
      skippedCount: syntheticGroups.length,
    };
  }

  if (syntheticGroups.length > 0) {
    return {
      params: {
        ids: syntheticGroups.flatMap((group) => group.transactionIds),
        size: GIFTING_EXPORT_SIZE,
      },
      skippedCount: 0,
    };
  }

  return { params: null, skippedCount: 0 };
};

/**
 * Group gifting transactions by their transaction group ID
 */
export const groupGiftingTransactions = (
  transactions: PointsTransaction[]
): GiftingTransactionGroup[] => {
  const grouped = transactions.reduce((acc, transaction) => {
    const groupId = getTransactionGroupId(transaction);

    const date = format(parseISO(transaction.createdAt), DEFAULT_DATE_FORMAT);
    const labelName = transaction.labelName || 'Employee Gift';
    const notes = transaction.notes || '';

    const reason = labelName.length > 20 ? `${labelName.substring(0, 20)}...` : labelName;
    const displayReason = labelName;

    if (!acc[groupId]) {
      acc[groupId] = {
        groupId,
        date,
        reason,
        displayReason,
        fullNotes: notes,
        totalAmount: 0,
        recipients: 0,
        transactions: [],
        transactionIds: [],
      };
    }

    acc[groupId].totalAmount += transaction.amount;
    acc[groupId].recipients += 1;
    acc[groupId].transactions.push(transaction);
    acc[groupId].transactionIds.push(transaction.id);

    return acc;
  }, {} as Record<string, GiftingTransactionGroup>);

  return Object.values(grouped);
};
